/**
 * Shared ability hover tooltip (#339, extracted for #410) — name (bold) +
 * the authored `description` text, plus (#412 v1) a fixed raw-facts block
 * below the description. Used by BOTH `ActionBar` (bottom horizontal bar —
 * anchors ABOVE the hovered slot) and `LoadoutPicker` (a multi-row grid —
 * anchors BELOW the hovered cell, since a cell in the grid's top row has no
 * room above it to render into without clipping against the viewport/panel
 * edge).
 *
 * `description === null` (no authored text yet) still shows a name-only
 * (plus facts, if any) tooltip rather than nothing, so hovering a
 * filled-but-undescribed slot/option isn't silently inert — same rationale
 * `ActionBar` originally shipped with.
 *
 * `facts === null` (no derived facts — e.g. a synthetic/test-only ability
 * id with no matching content JSON) renders no facts block at all, same
 * "absent means nothing to show" convention as `description`.
 *
 * #502: the facts block also lines out the granted-buff/debuff duration for
 * every aura the ability applies at top level (`facts.appliedAuraDefIds`,
 * see `abilityFacts.ts`) — the generic mechanism behind "Defensive Strike's
 * tooltip should show buff duration", built once for every ability rather
 * than hand-coded for that one. Resolved via `auraLabels.ts` at RENDER time
 * (not baked into `AbilityFacts`, which is ability-only content) — a
 * `scaledOnly` aura (e.g. Kidney Shot's stun, duration only known at cast
 * time from the caster's points) or an unknown def id formats to `null` and
 * is skipped, same "nothing legible to show" convention as `description`.
 */

import type { ReactElement } from "react";
import type { AbilityFacts } from "./abilityFacts.js";
import { auraDurationTicks, auraLabel, formatAuraDuration } from "./auraLabels.js";

/** Anchor side relative to the hovered element. `"above"` is `ActionBar`'s
 *  original single-row-bar behavior; `"below"` is `LoadoutPicker`'s
 *  multi-row-grid-safe behavior (see module doc comment). */
export type AbilityTooltipPlacement = "above" | "below";

/** Client tick rate — mirrors every other client call site's hardcoded
 *  `const TICK_RATE = 30` convention (see `ActionBar.tsx`/`AuraChip.tsx`
 *  and peers) rather than being threaded through from content at build
 *  time. */
const TICK_RATE = 30;

/** One "<aura name> duration: Xs" line per top-level `applyAura` def this
 *  ability carries, in authored order — skipping any def whose duration
 *  doesn't format to a static string (`scaledOnly`/unknown). */
function auraDurationLines(facts: AbilityFacts): readonly string[] {
  return (facts.appliedAuraDefIds ?? [])
    .map((defId) => {
      const formatted = formatAuraDuration(auraDurationTicks(defId), TICK_RATE);
      if (formatted === null) return null;
      return `${auraLabel(defId) ?? "Buff"} duration: ${formatted}`;
    })
    .filter((line): line is string => line !== null);
}

/** Flat `key: value` line list from the v1 whitelisted raw facts — raw
 *  units/ticks as-is, no unit conversion/pretty-formatting (deferred, see
 *  `abilityFacts.ts`) — EXCEPT the #502 buff-duration lines appended at the
 *  end, which ARE formatted (seconds), since a static tick count is not
 *  player-facing the way it is for cooldown/cast-time (already surfaced
 *  elsewhere, live, via the action-bar sweep). */
function factLines(facts: AbilityFacts): readonly string[] {
  const lines: string[] = [`Range: ${facts.rangeUnits}`];
  if (facts.minRangeUnits !== undefined) lines.push(`Min range: ${facts.minRangeUnits}`);
  lines.push(`Cooldown: ${facts.cooldownTicks} ticks`);
  lines.push(`Cast time: ${facts.castTimeTicks} ticks`);
  lines.push(`Cost: ${facts.cost}`);
  lines.push(`GCD: ${facts.gcd ? "yes" : "no"}`);
  if (facts.areaRadius !== undefined) lines.push(`Area radius: ${facts.areaRadius}`);
  if (facts.maxCharges !== undefined) lines.push(`Max charges: ${facts.maxCharges}`);
  lines.push(...auraDurationLines(facts));
  return lines;
}

function AbilityFactsBlock({ facts }: { readonly facts: AbilityFacts }): ReactElement {
  return (
    <div style={{ marginTop: 6, paddingTop: 4, borderTop: "1px solid #2a323c" }}>
      {factLines(facts).map((line) => (
        <div key={line}>{line}</div>
      ))}
    </div>
  );
}

export function AbilityTooltip({
  testId,
  name,
  description,
  facts = null,
  placement = "above",
}: {
  readonly testId: string;
  readonly name: string;
  readonly description: string | null;
  readonly facts?: AbilityFacts | null;
  readonly placement?: AbilityTooltipPlacement;
}): ReactElement {
  return (
    <div
      data-testid={testId}
      style={{
        position: "absolute",
        ...(placement === "above"
          ? { bottom: "calc(100% + 8px)" }
          : { top: "calc(100% + 8px)" }),
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 10,
        width: 180,
        padding: "8px 10px",
        background: "#11151b",
        border: "1px solid #3a4552",
        borderRadius: 6,
        color: "#dfe6ef",
        fontSize: 11,
        lineHeight: 1.4,
        textAlign: "left",
        pointerEvents: "none",
        boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: description !== null ? 4 : 0 }}>{name}</div>
      {description !== null ? <div>{description}</div> : null}
      {facts !== null ? <AbilityFactsBlock facts={facts} /> : null}
    </div>
  );
}
